import * as d3 from 'd3-selection'

const labelHeights = {
  'stage-number': 20,
  title: 20,
  value: 20,
  description: 18
}

const padding = 10

const stageRadius = labelHeights['stage-number'] / 1.3

const labelBuffer = 3

const descriptionHeight = 45

const getLabelOffsets = types => {
  let offset = 0
  return ['stage-number', 'title', 'value', 'description'] // Ensure this order
    .filter(type => types.includes(type))
    .reduce((p, type, i, all) => {
      if (i) {
        let prev = all[i - 1]
        offset = offset + labelHeights[prev]
      }

      p[type] = offset
      return p
    }, {})
}

const getLabels = (stage, config, totalHeight, formatter) => {
  let labels = []
    , runningHeight = 0
    , padding = 5 // space between each "row"
    , deniedSpace = false // If a previous label was denied, we're not going to show further labels. Top labels get priority

  let hasSpace = (runningHeight) => {
    if (typeof totalHeight === 'undefined') {
      return true // No validation
    }

    if (runningHeight < totalHeight && !deniedSpace) {
      return true
    } else {
      deniedSpace = true
      return false
    }
  }

  if (config.stage && hasSpace(labelHeights['stage-number'])) {
    labels.push({type: 'stage-number', text: stage.idx + 1})
    runningHeight += labelHeights['stage-number'] + labelBuffer
  }

  if (stage.title && hasSpace(runningHeight + labelHeights.title + padding)) {
    labels.push({type: 'title', text: stage.title, height: labelHeights.title})
    runningHeight += labelHeights.title + labelBuffer
  }

  if (config.value && hasSpace(runningHeight + labelHeights.value + padding)) {
    labels.push({type: 'value', text: formatter(stage.raw), height: labelHeights.value})
    runningHeight += labelHeights.value + labelBuffer
  }

  if (stage.subtitle && hasSpace(runningHeight + descriptionHeight + padding)) {
    labels.push({type: 'description', text: stage.subtitle, height: descriptionHeight}) // 45 gives us 3 lines
  }

  return labels
}


/*
 * We show potentially 4 different labels on a timeline:
 *
 * Name -> This is the serie.title
 * Description -> This is the serie.subtitle
 * Stage Number -> This is automatically generated, it's toggled by axes.infographic.stage
 * Value -> Show the value, it's toggled by axes.infographic.value
 *
 */
export default (selection, height, width, config) => {
  // Build a set of all the labels we show in each stage. This gets messy since
  // we need to look at the labels in each stage
  let labelTypes = Array.from(selection.data().map(d => getLabels(d, config, height, d.formatter)).reduce((set, c) => {
      c.forEach(label => set.add(label.type))
      return set
    }, new Set))
    , labelOffsets = getLabelOffsets(Array.from(labelTypes))

  selection.each(function (d) {
    let container = d3.select(this)
      , group = container.selectAll('g.label-group')
                         .data(d => [d])
                         .enter()
                         .append('g')
                           .attr('class', 'label-group')
                           .merge(container.selectAll('g.label-group'))
      , label = group.selectAll('g.label')
                     .data(d => {
                       let labels = getLabels(d, config, height, d.formatter)

                       labels.forEach(label => {
                         label.offset = labelOffsets[label.type]
                       })
                       return labels
                     }, d => d.type)

    label.exit().remove()

    let enter = label.enter()
                     .append('g')
                     .attr('class', d => `label ${d.type}`)

    label = label.merge(enter)

    // Stage label
    let stageEnter = enter.filter(d => d.type === 'stage-number')
    stageEnter.append('circle')
              .attr('r', stageRadius)

    stageEnter.append('text')
              .attr('font-size', stageRadius * 1.5)

    label.filter(d => d.type === 'stage-number')
         .attr('transform', `translate(${stageRadius}, 0)`)
         .select('text')
         .text(d => d.text)

    // Remaining labels. We use foreign objects for easy wrapping
    enter.filter(d => d.type !== 'stage-number')
         .append('foreignObject')
         .append('xhtml:div')
         .append('xhtml:p')

    label.filter(d => d.type !== 'stage-number')
         .attr('transform', d => `translate(0, ${labelOffsets[d.type]})`)
         .select('foreignObject')
         .attr('width', width - padding * 2)
         .attr('height', d => d.height)
         .select('p')
         .text(d => d.text)

    // Determine the height of the labels
    let labelHeight = group.node().getBBox().height

    // Position the labels so they are centered on the bar height
    group.attr('transform', d => {
           let center = height / 2 * d.directionMultiplier // Center of the vertical bar
             , offset = (labelHeight - stageRadius * 2) / 2

           return `translate(${padding}, ${center - offset})`
         })
  })
}
